import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { usePlayer } from '../../contexts/PlayerContext';
import { useLanguage, LANGUAGES } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatDuration } from '../../lib/utils';
import { saveTrackOffline, loadTrackOffline, getOfflineTracks, deleteOfflineTracks } from '../../lib/offlineStorage';
import { toast } from 'sonner';
import TravelerChatWidget from '../../components/TravelerChatWidget';
import TravelerAROverlay from '../../components/TravelerAROverlay';
import {
    ArrowLeft, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
    MapPin, Clock, Globe, Heart, Download, Share2, Info, ChevronDown,
    FileText, WifiOff, Loader2, Lock, Trash2, Music, Navigation, Eye, MessageCircle, Camera
} from 'lucide-react';
import AIVisualGuide from '../../components/AIVisualGuide';

const API = process.env.REACT_APP_BACKEND_URL;

function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function TravelerGuide() {
    const { attractionId } = useParams();
    const player = usePlayer();
    const { language: globalLanguage } = useLanguage();
    const [attraction, setAttraction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showInfo, setShowInfo] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
    const [downloadedTracks, setDownloadedTracks] = useState({});
    const [activeTab, setActiveTab] = useState('tracks');
    const [isFavorite, setIsFavorite] = useState(false);
    const [guideLang, setGuideLang] = useState(null); // null = not yet initialized
    const [gpsEnabled, setGpsEnabled] = useState(false);
    const [showAR, setShowAR] = useState(false);
    const [showVisualGuide, setShowVisualGuide] = useState(false);
    const [arAiFeatures, setArAiFeatures] = useState({ ar: false, ai: false });
    const [showMap, setShowMap] = useState(false);
    const [userPos, setUserPos] = useState(null);
    const [nearbyPois, setNearbyPois] = useState([]);

    // Walking Tour Mode state
    const [walkingTourActive, setWalkingTourActive] = useState(false);
    const [visitedPois, setVisitedPois] = useState(new Set());
    const [currentTourPoiId, setCurrentTourPoiId] = useState(null);

    const allTracks = attraction?.audio_content || [];
    const isThisGuide = player.attractionId === attractionId;
    const nowPlaying = isThisGuide ? player.currentTrackData : null;

    // Detect available languages from tracks
    const availableLanguages = useMemo(() => {
        const langSet = new Set();
        allTracks.forEach(t => { if (t.language) langSet.add(t.language.toLowerCase()); });
        return Array.from(langSet).sort();
    }, [allTracks]);

    // Initialize guide language: prefer global, fall back to first available
    useEffect(() => {
        if (availableLanguages.length > 0 && guideLang === null) {
            if (availableLanguages.includes(globalLanguage)) {
                setGuideLang(globalLanguage);
            } else {
                setGuideLang(availableLanguages[0]);
            }
        }
    }, [availableLanguages, globalLanguage, guideLang]);

    // Filtered tracks by selected language
    const audioContent = useMemo(() => {
        if (!guideLang) return allTracks;
        return allTracks.filter(t => t.language?.toLowerCase() === guideLang);
    }, [allTracks, guideLang]);

    // Fetch GPS settings to know if map should be shown
    const fetchGpsSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/gps-settings`);
            if (res.data.enabled && res.data.show_map) {
                setGpsEnabled(true);
                setShowMap(true);
            }
        } catch (e) {
            console.error('Failed to fetch GPS settings:', e);
        }
    }, []);

    const fetchAttraction = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/api/traveler/attraction/${attractionId}`, { withCredentials: true });
            setAttraction(response.data);
        } catch (error) {
            console.error('Failed to fetch attraction:', error);
        } finally {
            setLoading(false);
        }
    }, [attractionId]);

    const loadOfflineState = useCallback(async () => {
        try {
            const tracks = await getOfflineTracks(attractionId);
            setDownloadedTracks(tracks);
        } catch (e) {
            console.error('Failed to load offline state:', e);
        }
    }, [attractionId]);

    const checkFavorite = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/traveler/favorites`, { withCredentials: true });
            setIsFavorite(res.data.some(a => a.id === attractionId));
        } catch (e) {
            console.error('Failed to check favorite:', e);
        }
    }, [attractionId]);

    useEffect(() => {
        fetchAttraction();
        loadOfflineState();
        checkFavorite();
        fetchGpsSettings();
    }, [attractionId, fetchAttraction, loadOfflineState, checkFavorite, fetchGpsSettings]);

    // When GPS is enabled, try to get user position
    useEffect(() => {
        if (!gpsEnabled) return;
        if (!navigator.geolocation) return;
        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            },
            () => {
                setUserPos(null);
            },
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
        );
        return () => navigator.geolocation.clearWatch(watchId);
    }, [gpsEnabled]);

    // Fetch nearby POIs when we have user position
    useEffect(() => {
        if (!gpsEnabled || !userPos) return;
        const fetchNearby = async () => {
            try {
                const res = await axios.get(`${API}/api/traveler/nearby`, {
                    params: { lat: userPos.lat, lng: userPos.lng, attraction_id: attractionId },
                    withCredentials: true
                });
                if (res.data.enabled) setNearbyPois(res.data.pois || []);
            } catch (e) {
                console.error('Failed to fetch nearby POIs:', e);
            }
        };
        fetchNearby();
        const interval = setInterval(fetchNearby, 15000);
        return () => clearInterval(interval);
    }, [gpsEnabled, userPos, attractionId]);

    const toggleFavorite = async () => {
        try {
            if (isFavorite) {
                await axios.delete(`${API}/api/traveler/favorites/${attractionId}`, { withCredentials: true });
                setIsFavorite(false);
                toast.success('Removed from favorites');
            } else {
                await axios.post(`${API}/api/traveler/favorites/${attractionId}`, {}, { withCredentials: true });
                setIsFavorite(true);
                toast.success('Added to favorites');
            }
        } catch (e) {
            toast.error('Failed to update favorites');
        }
    };

    // Check AR/AI feature availability
    useEffect(() => {
        const checkFeatures = async () => {
            try {
                const arRes = await fetch(`${API}/api/traveler/attractions/${attractionId}/ar`, { credentials: 'include' });
                if (arRes.ok) {
                    const arData = await arRes.json();
                    setArAiFeatures(prev => ({ ...prev, ar: arData.length > 0 }));
                }
            } catch (e) {
                console.error('Failed to check AR features:', e);
            }
            try {
                const featRes = await fetch(`${API}/api/public/settings/features`, { credentials: 'include' });
                if (featRes.ok) {
                    const data = await featRes.json();
                    setArAiFeatures(prev => ({ ...prev, ai: data.ai_enabled || false }));
                }
            } catch (e) {
                console.error('Failed to check AI features:', e);
            }
        };
        if (attractionId) checkFeatures();
    }, [attractionId]);

    // Offline-aware audio resolver
    const audioResolver = useCallback(async (track) => {
        try {
            const blobUrl = await loadTrackOffline(attractionId, track.id);
            if (blobUrl) return blobUrl;
        } catch (e) { /* fall through to online */ }
        if (track.file_path) return `${API}/api/files/${track.file_path}`;
        return null;
    }, [attractionId]);

    const handlePlayTrack = (index) => {
        player.playTracks(audioContent, index, attractionId, attraction.title, audioResolver);
        // Log activity (fire and forget)
        const track = audioContent[index];
        if (track) {
            axios.post(`${API}/api/traveler/activity`, {
                type: 'listen',
                attraction_id: attractionId,
                attraction_title: attraction.title,
                track_id: track.id,
                track_title: track.title,
                language: track.language
            }, { withCredentials: true }).catch(() => {});
        }
    };

    // Walking Tour: auto-play when entering a new POI's radius
    useEffect(() => {
        if (!walkingTourActive || nearbyPois.length === 0) return;

        const withinRadius = nearbyPois.filter(p => p.within_radius && !visitedPois.has(p.id));
        if (withinRadius.length === 0) return;

        const closest = withinRadius[0];
        if (closest.id === currentTourPoiId) return;

        const trackIdx = audioContent.findIndex(t => t.id === closest.id);
        if (trackIdx === -1) return;

        const track = audioContent[trackIdx];
        player.playTracks([track], 0, attractionId, attraction?.title || '', audioResolver);
        setCurrentTourPoiId(closest.id);
        setVisitedPois(prev => new Set([...prev, closest.id]));

        toast.success(`Now playing: ${track.title}`, { description: 'Walking Tour', duration: 4000 });

        axios.post(`${API}/api/traveler/activity`, {
            type: 'listen',
            attraction_id: attractionId,
            attraction_title: attraction?.title,
            track_id: track.id,
            track_title: track.title,
            language: track.language
        }, { withCredentials: true }).catch(() => {});
    }, [walkingTourActive, nearbyPois, visitedPois, currentTourPoiId, audioContent, attractionId, attraction, audioResolver, player]);

    // Reset currentTourPoiId when player finishes
    useEffect(() => {
        if (walkingTourActive && !player.playing && currentTourPoiId) {
            setCurrentTourPoiId(null);
        }
    }, [walkingTourActive, player.playing, currentTourPoiId]);

    const startWalkingTour = () => {
        setWalkingTourActive(true);
        setVisitedPois(new Set());
        setCurrentTourPoiId(null);
        setActiveTab('map');
        toast.success('Walking Tour started! Walk toward a point of interest.', { duration: 5000 });
    };

    const stopWalkingTour = () => {
        setWalkingTourActive(false);
        setCurrentTourPoiId(null);
        toast.info('Walking Tour ended');
    };

    const downloadForOffline = async () => {
        if (!audioContent.length) return;
        setDownloading(true);
        const total = audioContent.filter(a => a.file_path).length;
        setDownloadProgress({ current: 0, total });
        let saved = 0;
        try {
            for (const audio of audioContent) {
                if (audio.file_path) {
                    try {
                        const response = await fetch(`${API}/api/files/${audio.file_path}`, { credentials: 'include' });
                        const arrayBuffer = await response.arrayBuffer();
                        await saveTrackOffline(attractionId, audio, arrayBuffer);
                        saved++;
                        setDownloadProgress({ current: saved, total });
                    } catch (e) {
                        console.error(`Failed to download ${audio.title}:`, e);
                    }
                }
            }
            await loadOfflineState();
            toast.success(`Encrypted & saved ${saved} tracks for offline listening`);
            // Log download activity
            axios.post(`${API}/api/traveler/activity`, {
                type: 'download',
                attraction_id: attractionId,
                attraction_title: attraction.title,
                track_title: `${saved} tracks`,
                language: guideLang || ''
            }, { withCredentials: true }).catch(() => {});
        } catch (error) {
            toast.error('Failed to download tracks');
        } finally {
            setDownloading(false);
            setDownloadProgress({ current: 0, total: 0 });
        }
    };

    const removeOfflineContent = async () => {
        try {
            await deleteOfflineTracks(attractionId);
            setDownloadedTracks({});
            toast.success('Offline content removed');
        } catch (e) {
            toast.error('Failed to remove offline content');
        }
    };

    const getImgUrl = (item) => {
        if (!item?.image_url) return null;
        return item.image_url.startsWith('http') ? item.image_url : `${API}${item.image_url}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!attraction) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-white text-xl mb-4">Attraction not found</h2>
                    <Link to="/my-guides">
                        <Button className="bg-gold text-[#0A0D14]">Back to My Guides</Button>
                    </Link>
                </div>
            </div>
        );
    }

    const hasOfflineContent = Object.keys(downloadedTracks).length > 0;

    return (
        <div className="min-h-screen bg-[#0A0D14] pb-8" data-testid="traveler-guide">
            {/* AR Overlay */}
            {showAR && (
                <TravelerAROverlay
                    attractionId={attractionId}
                    attractionTitle={attraction.title}
                    onClose={() => setShowAR(false)}
                />
            )}
            {/* AI Visual Guide Overlay */}
            {showVisualGuide && (
                <div className="fixed inset-0 z-[60] bg-[#0A0D14]/95 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-md h-[80vh] max-h-[600px] bg-[#121620] rounded-2xl border border-white/10 overflow-hidden flex flex-col shadow-2xl">
                        <AIVisualGuide language={guideLang || 'en'} onClose={() => setShowVisualGuide(false)} />
                    </div>
                </div>
            )}
            {/* Hero Section — taller for more visual impact */}
            <div className="relative h-[55vh] min-h-[350px]">
                {attraction.featured_image ? (
                    <img
                        src={attraction.featured_image}
                        alt={attraction.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/20 to-gold/5" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/40 to-black/20" />

                {/* Back */}
                <Link to="/my-guides" className="absolute top-4 left-3 sm:left-4 z-10">
                    <Button variant="ghost" size="icon" className="bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full" data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                </Link>

                {/* Top-right actions */}
                <div className="absolute top-4 right-3 sm:right-4 z-10 flex gap-1.5 sm:gap-2">
                    {hasOfflineContent && (
                        <div className="px-2 sm:px-3 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center gap-1 backdrop-blur-sm">
                            <Lock className="w-3 h-3" />
                            <span className="hidden sm:inline">Offline Ready</span>
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={toggleFavorite}
                        className={`bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full ${isFavorite ? 'text-red-400' : ''}`}
                        data-testid="favorite-btn"
                    >
                        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                    </Button>
                    <Button variant="ghost" size="icon" className="bg-black/50 backdrop-blur-sm hover:bg-black/70 rounded-full">
                        <Share2 className="w-5 h-5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowVisualGuide(true)}
                        className="bg-gold/20 backdrop-blur-sm hover:bg-gold/40 rounded-full text-gold"
                        data-testid="visual-guide-btn"
                        title="AI Visual Guide"
                    >
                        <Camera className="w-5 h-5" />
                    </Button>
                    {arAiFeatures.ar && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowAR(true)}
                            className="bg-[#D4AF37]/20 backdrop-blur-sm hover:bg-[#D4AF37]/40 rounded-full text-[#D4AF37]"
                            data-testid="ar-btn"
                        >
                            <Eye className="w-5 h-5" />
                        </Button>
                    )}
                </div>

                {/* Title overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 pb-6 sm:pb-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center gap-2 text-gold text-xs sm:text-sm mb-2 sm:mb-3">
                            <MapPin className="w-4 h-4" />
                            <span>{attraction.city_name}</span>
                            <span className="text-white/30">&middot;</span>
                            <span className="text-white/50">{attraction.category}</span>
                        </div>
                        <h1 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-3">
                            {attraction.title}
                        </h1>
                        <div className="flex items-center gap-3 sm:gap-5 text-xs sm:text-sm text-white/60 flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4" />
                                <span>{formatDuration(attraction.audio_duration)}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Globe className="w-4 h-4" />
                                <span>{availableLanguages.map(c => LANGUAGES[c]?.flag || c).join('  ')}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Music className="w-4 h-4" />
                                <span>{audioContent.length} track{audioContent.length !== 1 ? 's' : ''}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">

                {/* Walking Tour Active Banner */}
                {walkingTourActive && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-gold/15 via-gold/10 to-gold/5 border border-gold/30 animate-fade-in" data-testid="walking-tour-banner">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center animate-pulse">
                                    <Navigation className="w-5 h-5 text-gold" />
                                </div>
                                <div>
                                    <p className="text-white font-medium text-sm">Walking Tour Active</p>
                                    <p className="text-gold/70 text-xs">
                                        {(() => {
                                            const gpsTracks = audioContent.filter(t => t.latitude && t.longitude);
                                            const visited = gpsTracks.filter(t => visitedPois.has(t.id)).length;
                                            const total = gpsTracks.length;
                                            if (visited >= total && total > 0) return 'Tour complete!';
                                            if (currentTourPoiId) return 'Listening... walk to the next stop when ready';
                                            return `${visited}/${total} stops visited — walk toward a point of interest`;
                                        })()}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={stopWalkingTour}
                                className="border-gold/30 text-gold hover:bg-gold/10 text-xs"
                                data-testid="stop-tour-btn"
                            >
                                End Tour
                            </Button>
                        </div>
                        {/* Progress bar */}
                        {(() => {
                            const gpsTracks = audioContent.filter(t => t.latitude && t.longitude);
                            const total = gpsTracks.length;
                            if (total === 0) return null;
                            const visited = gpsTracks.filter(t => visitedPois.has(t.id)).length;
                            return (
                                <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                        className="h-full bg-gold rounded-full transition-all duration-700"
                                        style={{ width: `${(visited / total) * 100}%` }}
                                    />
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Language Selector */}
                {availableLanguages.length > 1 && (
                    <div data-testid="guide-language-selector" className="animate-fade-in">
                        <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Select Language</p>
                        <div className="flex flex-wrap gap-2">
                            {availableLanguages.map((code) => {
                                const lang = LANGUAGES[code];
                                const isSelected = guideLang === code;
                                const trackCount = allTracks.filter(t => t.language?.toLowerCase() === code).length;
                                return (
                                    <button
                                        key={code}
                                        onClick={() => setGuideLang(code)}
                                        className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border transition-all ${
                                            isSelected
                                                ? 'bg-gold/15 border-gold/40 text-white ring-1 ring-gold/30'
                                                : 'bg-[#121620] border-white/10 text-white/60 hover:bg-[#161b2a] hover:border-white/20'
                                        }`}
                                        data-testid={`guide-lang-${code}`}
                                    >
                                        <span className="text-lg leading-none">{lang?.flag || code.toUpperCase()}</span>
                                        <span className="text-sm font-medium">{lang?.nativeName || code.toUpperCase()}</span>
                                        <span className={`text-xs ml-1 ${isSelected ? 'text-gold' : 'text-white/30'}`}>({trackCount})</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* NOW PLAYING — large masterpiece image */}
                {nowPlaying && (
                    <div className="animate-slide-up" data-testid="now-playing-section">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                            <span className="text-sm font-medium text-gold">Now Playing</span>
                        </div>

                        {/* Large track image */}
                        <div className="rounded-2xl overflow-hidden relative mb-4">
                            {getImgUrl(nowPlaying) ? (
                                <img
                                    src={getImgUrl(nowPlaying)}
                                    alt={nowPlaying.title}
                                    className="w-full aspect-[16/10] object-cover"
                                    data-testid="now-playing-image"
                                />
                            ) : (
                                <div className="w-full aspect-[16/10] bg-gradient-to-br from-[#1a1f2e] to-[#0A0D14] flex items-center justify-center">
                                    <Music className="w-20 h-20 text-gold/20" />
                                </div>
                            )}
                            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
                                <h3 className="text-white font-outfit font-semibold text-base sm:text-xl mb-1">{nowPlaying.title}</h3>
                                <div className="flex items-center gap-3 text-sm text-white/60">
                                    <span className="uppercase">{nowPlaying.language}</span>
                                    {nowPlaying.transcript && (
                                        <span className="flex items-center gap-1 text-gold">
                                            <FileText className="w-3 h-3" />
                                            Transcript
                                        </span>
                                    )}
                                    {downloadedTracks[nowPlaying.id] && (
                                        <span className="flex items-center gap-1 text-emerald-400">
                                            <WifiOff className="w-3 h-3" />
                                            Offline
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Inline player controls */}
                        <div className="bg-[#121620] rounded-xl p-3 sm:p-5 border border-white/10">
                            {/* Progress bar */}
                            <div className="flex items-center gap-3 mb-4">
                                <span className="text-xs text-muted-foreground w-12 text-right font-mono">{formatTime(player.currentTime)}</span>
                                <Slider
                                    value={[player.currentTime]}
                                    max={player.duration || 100}
                                    step={0.1}
                                    onValueChange={(v) => player.seek(v[0])}
                                    className="flex-1"
                                    data-testid="seek-slider"
                                />
                                <span className="text-xs text-muted-foreground w-12 font-mono">{formatTime(player.duration)}</span>
                            </div>

                            {/* Controls row */}
                            <div className="flex items-center justify-between">
                                {/* Volume - hidden on mobile */}
                                <div className="hidden sm:flex items-center gap-2 w-36">
                                    <button onClick={player.toggleMute} className="p-2 text-muted-foreground hover:text-white transition-colors">
                                        {player.muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                    </button>
                                    <Slider
                                        value={[player.muted ? 0 : player.volume]}
                                        max={1}
                                        step={0.05}
                                        onValueChange={(v) => player.setVolume(v[0])}
                                        className="w-24"
                                    />
                                </div>

                                {/* Playback */}
                                <div className="flex items-center gap-3 sm:gap-4 mx-auto sm:mx-0">
                                    <button
                                        onClick={player.prevTrack}
                                        disabled={player.currentTrack === 0}
                                        className="p-2 text-muted-foreground hover:text-white disabled:opacity-30 transition-colors"
                                        data-testid="prev-track-btn"
                                    >
                                        <SkipBack className="w-6 h-6" />
                                    </button>
                                    <button
                                        onClick={player.togglePlay}
                                        className="w-14 h-14 rounded-full bg-gold flex items-center justify-center hover:bg-gold/90 transition-all active:scale-95"
                                        data-testid="play-pause-btn"
                                    >
                                        {player.playing ? (
                                            <Pause className="w-6 h-6 text-[#0A0D14]" />
                                        ) : (
                                            <Play className="w-6 h-6 text-[#0A0D14] ml-1" />
                                        )}
                                    </button>
                                    <button
                                        onClick={player.nextTrack}
                                        disabled={player.currentTrack >= player.tracks.length - 1}
                                        className="p-2 text-muted-foreground hover:text-white disabled:opacity-30 transition-colors"
                                        data-testid="next-track-btn"
                                    >
                                        <SkipForward className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Transcript shortcut */}
                                <div className="hidden sm:flex w-36 justify-end">
                                    {nowPlaying.transcript && (
                                        <button
                                            onClick={() => setActiveTab('transcript')}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 text-gold text-sm hover:bg-white/10 transition-colors"
                                        >
                                            <FileText className="w-4 h-4" />
                                            <span className="hidden sm:inline">Transcript</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Play All CTA — when not playing this guide */}
                {audioContent.length > 0 && !isThisGuide && (
                    <button
                        onClick={() => handlePlayTrack(0)}
                        className="w-full py-4 rounded-xl bg-gold/10 border border-gold/20 text-gold font-medium flex items-center justify-center gap-3 hover:bg-gold/20 transition-colors"
                        data-testid="play-all-btn"
                    >
                        <Play className="w-5 h-5" />
                        Play All Tracks
                    </button>
                )}

                {/* About Section */}
                <button
                    onClick={() => setShowInfo(!showInfo)}
                    className="w-full flex items-center justify-between p-4 rounded-xl bg-[#121620] border border-white/10 hover:border-white/15 transition-colors"
                    data-testid="about-toggle"
                >
                    <div className="flex items-center gap-3">
                        <Info className="w-5 h-5 text-gold" />
                        <span className="text-white font-medium">About this guide</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showInfo ? 'rotate-180' : ''}`} />
                </button>

                {showInfo && (
                    <div className="p-5 rounded-xl bg-[#121620] border border-white/10 animate-fade-in -mt-3">
                        <p className="text-muted-foreground leading-relaxed">{attraction.description}</p>
                        {attraction.opening_hours && (
                            <p className="mt-4 text-sm">
                                <span className="text-gold">Opening Hours:</span>{' '}
                                <span className="text-muted-foreground">{attraction.opening_hours}</span>
                            </p>
                        )}
                    </div>
                )}

                {/* Tabs: Tracks / Transcript */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <TabsList className="bg-[#121620] border border-white/10 w-full sm:w-auto">
                            <TabsTrigger value="tracks" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                                <Play className="w-4 h-4" />
                                Tracks ({audioContent.length})
                            </TabsTrigger>
                            {showMap && (
                                <TabsTrigger value="map" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2" data-testid="map-tab">
                                    <Navigation className="w-4 h-4" />
                                    Map
                                </TabsTrigger>
                            )}
                            <TabsTrigger value="transcript" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                                <FileText className="w-4 h-4" />
                                Transcript
                            </TabsTrigger>
                        </TabsList>

                        {audioContent.length > 0 && (
                            <div className="flex items-center gap-2">
                                {hasOfflineContent && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={removeOfflineContent}
                                        className="text-muted-foreground hover:text-red-400"
                                        data-testid="remove-offline-btn"
                                        title="Remove offline content"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadForOffline}
                                    disabled={downloading}
                                    className="border-white/10 text-muted-foreground hover:text-white gap-2"
                                    data-testid="download-offline-btn"
                                >
                                    {downloading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {downloadProgress.total > 0
                                                ? `${downloadProgress.current}/${downloadProgress.total}`
                                                : 'Encrypting...'}
                                        </>
                                    ) : hasOfflineContent ? (
                                        <>
                                            <Lock className="w-4 h-4 text-emerald-400" />
                                            Offline Ready
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4" />
                                            Save Offline
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Tracks list */}
                    <TabsContent value="tracks" className="space-y-3 mt-0">
                        {audioContent.length > 0 ? (
                            audioContent.map((audio, index) => {
                                const isCurrentTrack = isThisGuide && player.currentTrack === index;
                                const isPlaying = isCurrentTrack && player.playing;
                                const hasGps = audio.latitude && audio.longitude;
                                return (
                                    <button
                                        key={audio.id}
                                        onClick={() => handlePlayTrack(index)}
                                        className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all ${
                                            isCurrentTrack
                                                ? 'bg-gold/10 border-gold/30 ring-1 ring-gold/20'
                                                : 'bg-[#121620] border-white/10 hover:border-white/20 hover:bg-[#161b2a]'
                                        }`}
                                        data-testid={`audio-track-${index}`}
                                    >
                                        {/* Track image */}
                                        <div className={`relative w-14 h-14 sm:w-20 sm:h-20 rounded-xl overflow-hidden shrink-0 ${
                                            isPlaying ? 'ring-2 ring-gold animate-pulse-gold' : ''
                                        }`}>
                                            {getImgUrl(audio) ? (
                                                <img
                                                    src={getImgUrl(audio)}
                                                    alt={audio.title}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-[#1a1f2e] to-[#0d1017] flex items-center justify-center">
                                                    <Music className="w-8 h-8 text-gold/30" />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                {isPlaying ? (
                                                    <Pause className="w-5 h-5 text-white" />
                                                ) : (
                                                    <Play className="w-5 h-5 text-white ml-0.5" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Track info */}
                                        <div className="flex-1 text-left min-w-0">
                                            <p className={`font-medium truncate ${isCurrentTrack ? 'text-gold' : 'text-white'}`}>
                                                {audio.title}
                                            </p>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                                <span className="uppercase text-xs">{audio.language}</span>
                                                {audio.transcript && (
                                                    <span className="flex items-center gap-1 text-gold/70 text-xs">
                                                        <FileText className="w-3 h-3" />
                                                        Transcript
                                                    </span>
                                                )}
                                                {downloadedTracks[audio.id] && (
                                                    <span className="flex items-center gap-1 text-emerald-400/70 text-xs">
                                                        <WifiOff className="w-3 h-3" />
                                                        Offline
                                                    </span>
                                                )}
                                                {hasGps && (
                                                    <span className="flex items-center gap-1 text-blue-400/70 text-xs">
                                                        <MapPin className="w-3 h-3" />
                                                        GPS
                                                    </span>
                                                )}
                                            </div>
                                            {isCurrentTrack && (
                                                <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                                                    <div
                                                        className="h-full bg-gold rounded-full transition-all duration-300"
                                                        style={{ width: `${player.duration > 0 ? (player.currentTime / player.duration) * 100 : 0}%` }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Duration */}
                                        {audio.duration && (
                                            <span className="text-sm text-muted-foreground shrink-0">{formatDuration(audio.duration)}</span>
                                        )}
                                    </button>
                                );
                            })
                        ) : (
                            <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10">
                                <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">No audio content available yet.</p>
                            </div>
                        )}
                    </TabsContent>

                    {/* Interactive Map tab */}
                    {showMap && (
                        <TabsContent value="map" className="mt-0">
                            <TravelerMapView
                                pois={nearbyPois}
                                allTracks={audioContent}
                                userPos={userPos}
                                attractionId={attractionId}
                                onPlayTrack={handlePlayTrack}
                                player={player}
                                isThisGuide={isThisGuide}
                                walkingTourActive={walkingTourActive}
                                visitedPois={visitedPois}
                                currentTourPoiId={currentTourPoiId}
                                onStartTour={startWalkingTour}
                                onStopTour={stopWalkingTour}
                            />
                        </TabsContent>
                    )}

                    {/* Transcript tab */}
                    <TabsContent value="transcript" className="mt-0">
                        {(() => {
                            const transcriptTrack = isThisGuide ? player.currentTrackData : audioContent[0];
                            return transcriptTrack?.transcript ? (
                                <div className="p-6 rounded-xl bg-[#121620] border border-white/10">
                                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
                                        <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                                            <FileText className="w-5 h-5 text-gold" />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-medium">{transcriptTrack.title}</h3>
                                            <p className="text-sm text-muted-foreground">Transcript</p>
                                        </div>
                                    </div>
                                    <div className="prose prose-invert max-w-none">
                                        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                            {transcriptTrack.transcript}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10">
                                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-white font-medium mb-2">No transcript available</p>
                                    <p className="text-sm text-muted-foreground">
                                        {transcriptTrack ? 'This track has no transcript' : 'Select a track to view its transcript'}
                                    </p>
                                </div>
                            );
                        })()}

                        {audioContent.filter(a => a.transcript).length > 0 && (
                            <div className="mt-4">
                                <p className="text-sm text-muted-foreground mb-2">Tracks with transcripts:</p>
                                <div className="flex flex-wrap gap-2">
                                    {audioContent.map((audio, index) => audio.transcript && (
                                        <button
                                            key={audio.id}
                                            onClick={() => handlePlayTrack(index)}
                                            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                                isThisGuide && player.currentTrack === index
                                                    ? 'bg-gold text-[#0A0D14]'
                                                    : 'bg-white/10 text-muted-foreground hover:bg-white/20'
                                            }`}
                                        >
                                            {audio.title}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

// Lazy-loaded map component to avoid SSR issues
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';

const goldMarker = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-gold.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const blueMarker = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const greenMarker = new L.Icon({
    iconUrl: 'https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

function RecenterMap({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center) map.setView(center, map.getZoom());
    }, [center, map]);
    return null;
}

function TravelerMapView({ pois, allTracks, userPos, attractionId, onPlayTrack, player, isThisGuide, walkingTourActive, visitedPois, currentTourPoiId, onStartTour, onStopTour }) {
    // Derive map POIs from audioContent that have GPS coordinates
    const gpsTracksFromContent = useMemo(() => {
        return allTracks
            .filter(t => t.latitude && t.longitude)
            .map((t, idx) => ({
                ...t,
                trackIndex: allTracks.findIndex(a => a.id === t.id),
                distance: null,
                within_radius: false,
                trigger_radius: t.trigger_radius || 50
            }));
    }, [allTracks]);

    // Merge with nearby POI data for distance info
    const mapPois = useMemo(() => {
        const poiMap = {};
        pois.forEach(p => { poiMap[p.id] = p; });
        return gpsTracksFromContent.map(t => ({
            ...t,
            distance: poiMap[t.id]?.distance ?? null,
            within_radius: poiMap[t.id]?.within_radius ?? false
        }));
    }, [gpsTracksFromContent, pois]);

    // Determine center: first POI or user position or Rome default
    const center = useMemo(() => {
        if (mapPois.length > 0) return [mapPois[0].latitude, mapPois[0].longitude];
        if (userPos) return [userPos.lat, userPos.lng];
        return [41.8902, 12.4922];
    }, [mapPois, userPos]);

    const withinRadiusPois = mapPois.filter(p => p.within_radius);

    if (mapPois.length === 0) {
        return (
            <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10" data-testid="map-no-pois">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-white font-medium mb-2">No GPS points configured</p>
                <p className="text-sm text-muted-foreground">Audio tracks need GPS coordinates to appear on the map.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="traveler-map-view">
            {/* Walking Tour Controls */}
            {!walkingTourActive ? (
                <button
                    onClick={onStartTour}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/25 text-gold font-medium flex items-center justify-center gap-3 hover:from-gold/20 hover:to-gold/10 transition-all"
                    data-testid="start-walking-tour-btn"
                >
                    <Navigation className="w-5 h-5" />
                    Start Walking Tour
                    <span className="text-xs text-gold/50 ml-1">({mapPois.length} stops)</span>
                </button>
            ) : (
                <div className="space-y-3">
                    {/* Next Stop indicator */}
                    {(() => {
                        const nextUnvisited = mapPois.find(p => !visitedPois?.has(p.id) && p.id !== currentTourPoiId);
                        const allVisited = mapPois.every(p => visitedPois?.has(p.id));

                        if (allVisited) {
                            return (
                                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center" data-testid="tour-complete">
                                    <p className="text-emerald-400 font-medium">Tour Complete!</p>
                                    <p className="text-emerald-400/60 text-xs mt-1">You've visited all {mapPois.length} points of interest</p>
                                    <button
                                        onClick={onStopTour}
                                        className="mt-3 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/30 transition-colors"
                                        data-testid="finish-tour-btn"
                                    >
                                        Finish Tour
                                    </button>
                                </div>
                            );
                        }

                        if (currentTourPoiId) {
                            const current = mapPois.find(p => p.id === currentTourPoiId);
                            return current ? (
                                <div className="flex items-center gap-3 p-3 rounded-xl bg-gold/10 border border-gold/30 animate-fade-in" data-testid="now-playing-tour-poi">
                                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center animate-pulse">
                                        <Music className="w-5 h-5 text-gold" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white text-sm font-medium truncate">{current.title}</p>
                                        <p className="text-gold/70 text-xs">Now playing</p>
                                    </div>
                                </div>
                            ) : null;
                        }

                        return nextUnvisited ? (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#121620] border border-white/10" data-testid="next-stop-indicator">
                                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                                    <Navigation className="w-5 h-5 text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-white/40 uppercase tracking-wider">Next Stop</p>
                                    <p className="text-white text-sm font-medium truncate">{nextUnvisited.title}</p>
                                    <p className="text-muted-foreground text-xs">
                                        {nextUnvisited.distance != null ? `${Math.round(nextUnvisited.distance)}m away` : 'Walk toward this point'}
                                    </p>
                                </div>
                            </div>
                        ) : null;
                    })()}
                </div>
            )}

            {/* Proximity notifications */}
            {withinRadiusPois.length > 0 && (
                <div className="space-y-2 animate-fade-in">
                    {withinRadiusPois.map(poi => (
                        <div
                            key={poi.id}
                            className="flex items-center gap-3 p-3 rounded-xl bg-gold/10 border border-gold/30 cursor-pointer hover:bg-gold/20 transition-colors"
                            onClick={() => onPlayTrack(poi.trackIndex)}
                            data-testid={`nearby-notification-${poi.id}`}
                        >
                            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                                <Navigation className="w-5 h-5 text-gold" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-white text-sm font-medium truncate">{poi.title}</p>
                                <p className="text-gold text-xs">{Math.round(poi.distance)}m away — Tap to play</p>
                            </div>
                            <Play className="w-5 h-5 text-gold shrink-0" />
                        </div>
                    ))}
                </div>
            )}

            {/* The Map */}
            <div className="rounded-xl overflow-hidden border border-white/10 h-[400px]" data-testid="leaflet-map">
                <MapContainer center={center} zoom={16} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* User position */}
                    {userPos && (
                        <Circle center={[userPos.lat, userPos.lng]} radius={8} pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.8 }}>
                            <Popup>Your location</Popup>
                        </Circle>
                    )}
                    {/* POI markers */}
                    {mapPois.map(poi => {
                        const isVisited = visitedPois?.has(poi.id);
                        const isCurrent = currentTourPoiId === poi.id;
                        const markerIcon = isVisited ? greenMarker : (isCurrent || poi.within_radius) ? goldMarker : blueMarker;
                        return (
                        <Marker
                            key={poi.id}
                            position={[poi.latitude, poi.longitude]}
                            icon={markerIcon}
                            eventHandlers={{ click: () => onPlayTrack(poi.trackIndex) }}
                        >
                            <Popup>
                                <div className="text-center min-w-[140px]">
                                    <p className="font-semibold text-sm">{poi.title}</p>
                                    <p className="text-xs text-gray-500">{poi.language?.toUpperCase()}</p>
                                    {isVisited && <p className="text-xs text-green-600 font-medium mt-1">Visited</p>}
                                    {poi.distance != null && <p className="text-xs mt-1">{Math.round(poi.distance)}m away</p>}
                                    <button
                                        onClick={() => onPlayTrack(poi.trackIndex)}
                                        className="mt-2 px-3 py-1 bg-amber-500 text-white text-xs rounded-full hover:bg-amber-600"
                                    >
                                        {isVisited ? 'Replay Audio' : 'Play Audio'}
                                    </button>
                                </div>
                            </Popup>
                            <Circle
                                center={[poi.latitude, poi.longitude]}
                                radius={poi.trigger_radius}
                                pathOptions={{
                                    color: isVisited ? '#22c55e' : (isCurrent || poi.within_radius) ? '#D4AF37' : '#6366f1',
                                    fillColor: isVisited ? '#22c55e' : (isCurrent || poi.within_radius) ? '#D4AF37' : '#6366f1',
                                    fillOpacity: 0.1,
                                    weight: 1
                                }}
                            />
                        </Marker>
                        );
                    })}
                </MapContainer>
            </div>

            {/* POI list below map */}
            <div className="space-y-2">
                <p className="text-xs text-white/40 uppercase tracking-wider">Points of Interest ({mapPois.length})</p>
                {mapPois.map((poi, idx) => {
                    const isPlaying = isThisGuide && player.currentTrack === poi.trackIndex && player.playing;
                    const isVisited = visitedPois?.has(poi.id);
                    const isCurrent = currentTourPoiId === poi.id;
                    return (
                        <button
                            key={poi.id}
                            onClick={() => onPlayTrack(poi.trackIndex)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                isPlaying || isCurrent
                                    ? 'bg-gold/10 border-gold/30'
                                    : isVisited
                                        ? 'bg-emerald-500/5 border-emerald-500/20'
                                        : poi.within_radius
                                            ? 'bg-blue-500/5 border-blue-500/20 hover:bg-blue-500/10'
                                            : 'bg-[#121620] border-white/10 hover:border-white/20'
                            }`}
                            data-testid={`map-poi-${poi.id}`}
                        >
                            {/* Step number / status icon */}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                isVisited ? 'bg-emerald-500/20' : isCurrent ? 'bg-gold/20' : poi.within_radius ? 'bg-blue-500/10' : 'bg-white/5'
                            }`}>
                                {isVisited ? (
                                    <svg className="w-4 h-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" /></svg>
                                ) : (
                                    <span className={`text-xs font-bold ${isCurrent ? 'text-gold' : 'text-muted-foreground'}`}>{idx + 1}</span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isPlaying || isCurrent ? 'text-gold' : isVisited ? 'text-emerald-400' : 'text-white'}`}>{poi.title}</p>
                                <p className="text-xs text-muted-foreground">
                                    {poi.distance != null ? `${Math.round(poi.distance)}m` : 'Distance unknown'}
                                    {isVisited && <span className="text-emerald-400 ml-2">Visited</span>}
                                    {isCurrent && <span className="text-gold ml-2">Now Playing</span>}
                                    {poi.within_radius && !isVisited && !isCurrent && <span className="text-blue-400 ml-2">Within range</span>}
                                </p>
                            </div>
                            {isPlaying ? (
                                <Pause className="w-4 h-4 text-gold shrink-0" />
                            ) : isVisited ? (
                                <Play className="w-4 h-4 text-emerald-400 shrink-0" />
                            ) : (
                                <Play className="w-4 h-4 text-muted-foreground shrink-0" />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
